import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminNewsArticle } from '@/lib/api'
import NewsEditor, { type NewsEditorPayload } from '@/components/news/NewsEditor'

function isHtmlContent(raw: string): boolean {
  const trimmed = raw.trim()
  return trimmed.startsWith('<') || trimmed === ''
}

export default function NewsFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()

  const isEdit = Boolean(id)

  const [article, setArticle] = useState<AdminNewsArticle | undefined>(undefined)
  const [initialContent, setInitialContent] = useState<string>('')
  const [loading, setLoading] = useState(isEdit)

  useEffect(() => {
    if (!isEdit || !token) return

    const stateArticle = (location.state as { article?: AdminNewsArticle } | null)?.article
    if (stateArticle) {
      setArticle(stateArticle)
      setInitialContent(isHtmlContent(stateArticle.content) ? stateArticle.content : '')
      setLoading(false)
      return
    }

    api.getAdminNews(token).then((items) => {
      const found = items.find((a) => a.id === id)
      if (found) {
        setArticle(found)
        setInitialContent(isHtmlContent(found.content) ? found.content : '')
      }
    }).finally(() => setLoading(false))
  }, [id, isEdit, token, location.state])

  const handleSubmit = async (payload: NewsEditorPayload) => {
    if (!token) return
    if (isEdit && id) {
      const updated = await api.updateNews(token, id, payload)
      navigate(`/news/${id}`, { replace: true, state: { article: updated } })
    } else {
      await api.createNews(token, payload)
      navigate(-1)
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-zinc-500 text-sm">Loading…</p>
      </div>
    )
  }

  return (
    <NewsEditor
      mode={isEdit ? 'edit' : 'create'}
      initialData={article}
      initialContent={initialContent}
      onSubmit={handleSubmit}
      onCancel={() => navigate(-1)}
    />
  )
}
